/*======================================================================================================================
  * Bluebell: src/app/state/kinematic-models/kinematic-model.effects.ts
  * Last Edited: 15/01/2025
  * Copyright 2025 Bramble CFD LTD
  * The contents of this file are NOT for redistribution
  * See AUTHORS for list of developers on this project
 =====================================================================================================================*/
import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EffectHandlerService } from '@services/effect-handler.service';
import { ApiService } from '@state/api.service';
import {
  createKinematicModel,
  createKinematicModelFailure,
  createKinematicModelSuccess,
  deleteKinematicModel,
  deleteKinematicModelSuccess,
  deleteKinematicModelFailure,
  listKinematicModels,
  listKinematicModelsFailure,
  listKinematicModelsSuccess,
  retrieveKinematicModel,
  retrieveKinematicModelFailure,
  retrieveKinematicModelSuccess,
  updateKinematicModel,
  updateKinematicModelFailure,
  updateKinematicModelSuccess,
} from '@state/kinematic-models/kinematic-model.actions';
import { KinematicModel, KinematicModelBase } from '@state/kinematic-models/kinematic-model.interfaces';
import {
  selectKinematicSetupSuccess,
} from '@state/kinematic-setups/kinematic-setup.actions';
import { KinematicSetup } from '@state/kinematic-setups/kinematic-setup.interfaces';
import { concatMap, mergeMap, switchMap } from 'rxjs';
import { filter, map } from 'rxjs/operators';


@Injectable()
export class KinematicModelEffects {
  actions$ = inject(Actions);
  api = inject(ApiService);
  handler = inject(EffectHandlerService);

  create$ = createEffect(() => this.actions$.pipe(
    ofType(createKinematicModel),
    mergeMap(({ data }) => this.api.createKinematicModel(data).pipe(
      this.handler.effectResponse({
        success: (selected: KinematicModel) => createKinematicModelSuccess({ selected }),
        retry: createKinematicModel({ data }),
        failure: createKinematicModelFailure(),
        failureMessage: 'Create kinematic model failed'
      })
    ))
  ));

  list$ = createEffect(() => this.actions$.pipe(
    ofType(listKinematicModels),
    switchMap(() => this.api.listKinematicModels().pipe(
      this.handler.effectResponse({
        success: (models: KinematicModelBase[]) => listKinematicModelsSuccess({ models }),
        retry: listKinematicModels(),
        failure: listKinematicModelsFailure(),
        failureMessage: 'Load kinematic models failed'
      }),
    ))
  ));

  retrieve$ = createEffect(() => this.actions$.pipe(
    ofType(retrieveKinematicModel),
    switchMap(({ uuid }) => this.api.retrieveKinematicModel(uuid).pipe(
      this.handler.effectResponse({
        success: (selected: KinematicModel) => retrieveKinematicModelSuccess({ selected }),
        retry: retrieveKinematicModel({ uuid }),
        failure: retrieveKinematicModelFailure(),
        failureMessage: 'Load kinematic models failed'
      }),
    ))
  ));

  update$ = createEffect(() => this.actions$.pipe(
    ofType(updateKinematicModel),
    concatMap(({ model }) => this.api.updateKinematicModel(model).pipe(
      this.handler.effectResponse({
        success: (updatedModel: KinematicModel) => updateKinematicModelSuccess({ updatedModel }),
        retry: updateKinematicModel({ model }),
        failure: updateKinematicModelFailure(),
        failureMessage: 'Update kinematic model failed'
      }),
    ))
  ));

  delete$ = createEffect(() => this.actions$.pipe(
    ofType(deleteKinematicModel),
    mergeMap(({ uuid }) => this.api.deleteKinematicModel(uuid).pipe(
      this.handler.effectResponse({
        success: (setup_uuid: string) => deleteKinematicModelSuccess({ uuid, setup_uuid }),
        retry: deleteKinematicModel({ uuid }),
        failure: deleteKinematicModelFailure(),
        failureMessage: 'Delete kinematic model failed'
      }),
    ))
  ));

  retrieveWhenSetupSelected$ = createEffect(() => this.actions$.pipe(
    ofType(selectKinematicSetupSuccess),
    filter((payload): payload is ReturnType<typeof selectKinematicSetupSuccess> & { selected: KinematicSetup & { model: string } }  => !!payload.selected.model),
    map(({ selected }) => retrieveKinematicModel({ uuid: selected.model }))
  ));

  // clearWhenSetupChanged$ = createEffect(() => this.actions$.pipe(
  //   ofType(clearSelectedKinematicSetup),
  //   map(() => clearSelectedKinematicModel())
  //   ));

}

